// useAppOffsets.js — v200.29 (2026-02-09)
//
// Observes header/tabs/mobile-nav/bottom-bar heights via ResizeObserver and
// writes three CSS custom properties on <html>:
//
//   --app-top-offset      : sum of fixed elements at top (hdr + tabs OR mob-nav)
//   --app-hdr-offset      : just the fixed header height (used by .tabs top)
//   --app-bottom-offset   : the fixed mobile bottom bar height (if visible)
//
// v200.29 · Section (د) — EnvBanner was DELETED.
// v11 (2026-02-17) · Iter41 — MobileBottomTabBar (.mobile-bottom-bar) deleted;
// only .mbb remains as the mobile bottom nav.
//
// safe-area-inset-* is already contained inside each element's own padding
// (see .hdr, .mob-nav, .mbb rules), so getBoundingClientRect() naturally
// accounts for it. No hardcoded pixel values live in CSS anymore.
import { useLayoutEffect } from "react";

const SELECTORS = [".hdr", ".tabs-grouped", ".mob-nav", ".mbb"];

function isFixedVisible(el) {
  if (!el) return { visible: false, fixed: false, h: 0 };
  const cs = window.getComputedStyle(el);
  if (cs.display === "none" || cs.visibility === "hidden") {
    return { visible: false, fixed: false, h: 0 };
  }
  const rect = el.getBoundingClientRect();
  return {
    visible: rect.height > 0,
    fixed: cs.position === "fixed" || cs.position === "sticky",
    h: rect.height,
  };
}

export default function useAppOffsets() {
  useLayoutEffect(() => {
    const root = document.documentElement;

    const measure = () => {
      const hdr = document.querySelector(".hdr");
      const tabs = document.querySelector(".tabs-grouped");
      const mobNav = document.querySelector(".mob-nav");
      const mbb = document.querySelector(".mbb");

      const H = isFixedVisible(hdr);
      const T = isFixedVisible(tabs);
      const M = isFixedVisible(mobNav);
      const B = isFixedVisible(mbb);

      // v200.29 · env-banner deleted. Only real fixed top-stack layers
      // contribute now: hdr(fixed) + tabs(fixed). On mobile hdr becomes
      // position:relative so mob-nav takes over.
      const topOffset =
        (H.visible && H.fixed ? H.h : 0) +
        (T.visible && T.fixed ? T.h : 0) +
        (M.visible && M.fixed ? M.h : 0);

      const hdrOffset = H.visible && H.fixed ? H.h : 0;

      const bottomOffset = B.visible && B.fixed ? B.h : 0;

      root.style.setProperty("--app-top-offset", `${Math.ceil(topOffset)}px`);
      root.style.setProperty("--app-hdr-offset", `${Math.ceil(hdrOffset)}px`);
      root.style.setProperty(
        "--app-bottom-offset",
        `${Math.ceil(bottomOffset)}px`
      );
    };

    // Initial measure — synchronous so first paint sees correct offsets.
    measure();

    // ResizeObserver on every candidate element that is currently in the DOM.
    const ro = new ResizeObserver(measure);
    SELECTORS.forEach((sel) => {
      const el = document.querySelector(sel);
      if (el) ro.observe(el);
    });

    // Window resize + orientation + media-query breakpoint changes.
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    const mq = window.matchMedia("(max-width: 900px)");
    const mqMobile = window.matchMedia("(max-width: 640px)");
    const onMq = () => measure();
    if (mq.addEventListener) mq.addEventListener("change", onMq);
    else mq.addListener(onMq);
    if (mqMobile.addEventListener) mqMobile.addEventListener("change", onMq);
    else mqMobile.addListener(onMq);

    // Late-mount safety: MobileNavDrawer / MobileBottomBar sometimes render
    // in a subsequent commit — re-measure after the next paint and again after
    // 250ms so we pick them up regardless.
    const raf = requestAnimationFrame(measure);
    const t = setTimeout(measure, 250);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      ro.disconnect();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      if (mq.removeEventListener) mq.removeEventListener("change", onMq);
      else mq.removeListener(onMq);
      if (mqMobile.removeEventListener) mqMobile.removeEventListener("change", onMq);
      else mqMobile.removeListener(onMq);
    };
  }, []);
}
